import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type LayoutChangeEvent, Platform, StyleSheet, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  cancelAnimation,
  interpolate,
  runOnJS,
  useAnimatedReaction,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { SvgIcon } from '../../SvgIcon';
import { layoutLtrStyle } from '../../../../shared/i18n/rtl';
import { ScreenWidth } from './constants';
import { TickRuler } from './TickRuler';
import { snapPoint } from '../utils/snapPoint';
import { unwrapReanimatedValue } from '../utils/unwrapReanimatedValue';

import type { SharedValue } from 'react-native-reanimated';

const IDLE_PAN_PX = 1;
const IDLE_ARROW_PX = 20;
const IDLE_ARROW_INSET = 8;
const IDLE_HINT_TRANSLATE = 3.5;
const IDLE_HINT_SCALE = 0.1;
const IDLE_OPACITY_MIN = 0.4;
const IDLE_BREATHE_MS = 700;
const DEFAULT_IDLE_ARROWS = '#8e8e93';
/** Soft resistance past the real min/max while dragging. */
const EDGE_RUBBER = 0.34;
/** Default ghost ticks past each end of the value range. */
const DEFAULT_OVERSCROLL_TICKS = 12;

const SNAP_SPRING = {
  mass: 0.24,
  damping: 22,
  stiffness: 195,
  overshootClamping: true,
};

type SnapSpringConfig = {
  mass?: number;
  damping?: number;
  stiffness?: number;
  overshootClamping?: boolean;
};

type DraggableSliderProps = {
  linesAmount: number;
  spacePerLine: SharedValue<number> | number;
  maxLineHeight: number;
  minLineHeight: number;
  bigLineIndexOffset?: number;
  /** Value at first tick — used so major ticks land on real multiples (e.g. age 10/20/30). */
  lineValueBase?: number;
  lineWidth?: number;
  scrollableAreaHeight: number;
  snapEach?: number;
  onProgressChange?: (progress: number) => void;
  indicatorColor?: SharedValue<string>;
  markerColor?: string;
  lineColor?: string;
  bigLineColor?: string;
  initialProgress?: number | null;
  lineCanvasPaddingTop?: number;
  rulerStopsMode?: boolean;
  idleArrowsAfterMs?: number;
  idleArrowsColor?: string;
  onGestureActiveChange?: (active: boolean) => void;
  /**
   * @deprecated Numeric rulers always use LTR pan (EN digits). Kept for API compat; ignored.
   */
  isRTL?: boolean;
  snapSpring?: SnapSpringConfig;
  /** Decorative ticks past min/max (value still clamps). */
  overscrollTicks?: number;
};

export const DraggableSlider: React.FC<DraggableSliderProps> = ({
  linesAmount,
  spacePerLine: spacePerLineProp,
  maxLineHeight,
  minLineHeight,
  bigLineIndexOffset = 10,
  lineValueBase = 0,
  lineWidth = 1.5,
  scrollableAreaHeight,
  onProgressChange,
  snapEach = 1,
  indicatorColor,
  markerColor: markerColorProp,
  lineColor = '#c6c6c6',
  bigLineColor = '#c6c6c6',
  initialProgress = null,
  lineCanvasPaddingTop: lineCanvasPaddingTopProp,
  rulerStopsMode: rulerStops = false,
  idleArrowsAfterMs: idleArrowsAfterMsProp,
  idleArrowsColor = DEFAULT_IDLE_ARROWS,
  onGestureActiveChange,
  isRTL: _isRTL = false,
  snapSpring: snapSpringProp,
  overscrollTicks: overscrollTicksProp,
}) => {
  const overscroll_ticks = overscrollTicksProp ?? DEFAULT_OVERSCROLL_TICKS;
  const snap_spring = useMemo(
    () => ({ ...SNAP_SPRING, ...snapSpringProp }),
    [snapSpringProp?.mass, snapSpringProp?.damping, snapSpringProp?.stiffness, snapSpringProp?.overshootClamping],
  );
  const line_canvas_inner_pad =
    lineCanvasPaddingTopProp != null
      ? lineCanvasPaddingTopProp
      : scrollableAreaHeight / 2 - maxLineHeight / 2;
  const [measured_w, setMeasuredW] = useState(ScreenWidth);
  const [spacing_px, setSpacingPx] = useState(() => unwrapReanimatedValue(spacePerLineProp));
  const layout_width = useSharedValue(ScreenWidth);
  const idle_hints_on = idleArrowsAfterMsProp != null && idleArrowsAfterMsProp > 0;
  const idle_arrows_after_ms = idleArrowsAfterMsProp ?? 0;
  const [show_idle_hints, setShowIdleHints] = useState(false);
  const hint_idle_timer_ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hint_pan_trip_ref = useSharedValue(0);
  const idle_arrows_on_sv = useSharedValue(0);
  const marker_pulse = useSharedValue(1);
  const previous_line_index = useSharedValue(-1);
  const gesturing_ref = useRef(false);

  useLayoutEffect(() => {
    idle_arrows_on_sv.value = idle_hints_on ? 1 : 0;
  }, [idleArrowsAfterMsProp, idle_arrows_on_sv, idle_hints_on]);

  const sync_spacing = useCallback((next: number) => {
    setSpacingPx(next);
  }, []);

  useLayoutEffect(() => {
    setSpacingPx(unwrapReanimatedValue(spacePerLineProp));
  }, [spacePerLineProp]);

  useAnimatedReaction(
    () => unwrapReanimatedValue(spacePerLineProp),
    (curr, prev) => {
      if (curr !== prev) {
        runOnJS(sync_spacing)(curr);
      }
    },
    [spacePerLineProp],
  );

  const clear_idle_show_timer = useCallback(() => {
    if (hint_idle_timer_ref.current != null) {
      clearTimeout(hint_idle_timer_ref.current);
      hint_idle_timer_ref.current = null;
    }
  }, []);

  const schedule_idle_show = useCallback(() => {
    if (!idle_hints_on) {
      return;
    }
    clear_idle_show_timer();
    hint_idle_timer_ref.current = setTimeout(() => {
      setShowIdleHints(true);
    }, idle_arrows_after_ms);
  }, [clear_idle_show_timer, idle_arrows_after_ms, idle_hints_on]);

  const on_idle_first_move = useCallback(() => {
    clear_idle_show_timer();
    setShowIdleHints(false);
  }, [clear_idle_show_timer]);

  const on_idle_pan_end = useCallback(() => {
    if (!idle_hints_on) {
      return;
    }
    schedule_idle_show();
  }, [idle_hints_on, schedule_idle_show]);

  const notify_gesture_active = useCallback(
    (active: boolean) => {
      gesturing_ref.current = active;
      onGestureActiveChange?.(active);
    },
    [onGestureActiveChange],
  );

  /** Stable JS bridges so Gesture.Pan is not torn down/rebuilt mid-drag. */
  const notify_gesture_active_ref = useRef(notify_gesture_active);
  notify_gesture_active_ref.current = notify_gesture_active;
  const on_idle_first_move_ref = useRef(on_idle_first_move);
  on_idle_first_move_ref.current = on_idle_first_move;
  const on_idle_pan_end_ref = useRef(on_idle_pan_end);
  on_idle_pan_end_ref.current = on_idle_pan_end;

  const emit_gesture_active = useCallback((active: boolean) => {
    notify_gesture_active_ref.current(active);
  }, []);
  const emit_idle_first_move = useCallback(() => {
    on_idle_first_move_ref.current();
  }, []);
  const emit_idle_pan_end = useCallback(() => {
    on_idle_pan_end_ref.current();
  }, []);

  useEffect(() => {
    if (!idle_hints_on) {
      clear_idle_show_timer();
      setShowIdleHints(false);
      return;
    }
    schedule_idle_show();
    return () => {
      clear_idle_show_timer();
    };
  }, [clear_idle_show_timer, idle_hints_on, schedule_idle_show, idleArrowsAfterMsProp]);

  const idle_hint_breathe = useSharedValue(0);

  const r_idle_hint_start_style = useAnimatedStyle(() => {
    const b = idle_hint_breathe.value;
    return {
      opacity: IDLE_OPACITY_MIN + (1 - IDLE_OPACITY_MIN) * b,
      transform: [
        { translateX: -IDLE_HINT_TRANSLATE * b },
        { scale: 1 + IDLE_HINT_SCALE * b },
      ],
    };
  });

  const r_idle_hint_end_style = useAnimatedStyle(() => {
    const b = idle_hint_breathe.value;
    return {
      opacity: IDLE_OPACITY_MIN + (1 - IDLE_OPACITY_MIN) * b,
      transform: [
        { translateX: IDLE_HINT_TRANSLATE * b },
        { scale: 1 + IDLE_HINT_SCALE * b },
      ],
    };
  });

  useEffect(() => {
    if (!show_idle_hints) {
      cancelAnimation(idle_hint_breathe);
      idle_hint_breathe.value = 0;
      return;
    }
    cancelAnimation(idle_hint_breathe);
    idle_hint_breathe.value = 0;
    idle_hint_breathe.value = withRepeat(
      withSequence(
        withTiming(1, { duration: IDLE_BREATHE_MS }),
        withTiming(0, { duration: IDLE_BREATHE_MS }),
      ),
      -1,
      false,
    );
  }, [show_idle_hints, idle_hint_breathe]);

  const scroll_context = useSharedValue(0);
  const scroll_offset = useSharedValue(ScreenWidth / 2);
  const initial_p = useSharedValue(
    initialProgress != null && initialProgress >= 0 && initialProgress <= 1 ? initialProgress : -1,
  );
  const has_applied_initial = useSharedValue(false);

  useLayoutEffect(() => {
    if (initialProgress == null) {
      initial_p.value = -1;
      return;
    }
    initial_p.value = initialProgress;
    // Never yank the ruler mid-drag when parent re-renders the controlled value.
    if (!gesturing_ref.current) {
      has_applied_initial.value = false;
    }
  }, [initialProgress, has_applied_initial, initial_p]);

  const space_per_line = useDerivedValue(() => {
    return unwrapReanimatedValue(spacePerLineProp);
  }, [spacePerLineProp]);

  const progress_width = useDerivedValue(() => {
    const s = space_per_line.value;
    if (rulerStops && linesAmount > 1) {
      return (linesAmount - 1) * s;
    }
    return Math.round(linesAmount * s);
  }, [linesAmount, space_per_line, rulerStops]);

  const snap_offsets = useDerivedValue(() => {
    const half = layout_width.value / 2;
    const s = space_per_line.value;
    if (rulerStops) {
      const n = linesAmount;
      if (n <= 0) {
        return [half];
      }
      const se = Math.max(1, snapEach);
      const m = 1 + Math.floor((n - 1) / se);
      return Array.from({ length: m }, (_, j) => half - j * se * s);
    }
    const count = Math.round(linesAmount / snapEach) + 1;
    return Array.from({ length: count }, (_, i) => half - i * s * snapEach);
  }, [linesAmount, layout_width, rulerStops, snapEach, space_per_line]);

  const apply_scroll_for_progress = (progress: number, track_width: number, span_width: number) => {
    'worklet';
    const half = track_width / 2;
    if (span_width <= 0) {
      scroll_offset.value = half;
      return;
    }
    const clamped = Math.max(0, Math.min(1, progress));
    scroll_offset.value = half - clamped * span_width;
  };

  useAnimatedReaction(
    () => [progress_width.value, initial_p.value, has_applied_initial.value, layout_width.value] as const,
    ([span_width, progress, done, track_width]) => {
      if (done) {
        return;
      }
      if (progress < 0) {
        has_applied_initial.value = true;
        return;
      }
      if (track_width <= 0) {
        return;
      }
      apply_scroll_for_progress(progress, track_width, span_width);
      has_applied_initial.value = true;
    },
  );

  useAnimatedReaction(
    () => [scroll_offset.value, progress_width.value, layout_width.value] as const,
    (current, previous) => {
      if (previous == null) {
        return;
      }
      const [offset, span_width, track_width] = current;
      if (track_width <= 0) {
        return;
      }
      const half = track_width / 2;
      if (span_width <= 0) {
        onProgressChange?.(0);
        return;
      }
      const progress = interpolate(offset, [half, half - span_width], [0, 1], 'clamp');
      const line_span = linesAmount > 1 ? linesAmount - 1 : 1;
      const line_index =
        linesAmount <= 1
          ? 0
          : Math.min(Math.max(0, Math.round(progress * line_span)), linesAmount - 1);
      if (line_index !== previous_line_index.value) {
        previous_line_index.value = line_index;
        /** Light tick — avoids stacked springs while flinging across long ranges. */
        marker_pulse.value = withSequence(
          withTiming(1.05, { duration: 45 }),
          withTiming(1, { duration: 110 }),
        );
      }
      onProgressChange?.(progress);
    },
    [linesAmount, onProgressChange],
  );

  const gesture = useMemo(
    () =>
      Gesture.Pan()
        .activeOffsetX([-2, 2])
        .failOffsetY(Platform.OS === 'android' ? [-22, 22] : [-16, 16])
        .onBegin(() => {
          'worklet';
          scroll_context.value = scroll_offset.value;
          cancelAnimation(scroll_offset);
          hint_pan_trip_ref.value = 0;
          runOnJS(emit_gesture_active)(true);
        })
        .onUpdate(event => {
          'worklet';
          if (Math.abs(event.translationX) > IDLE_PAN_PX) {
            if (hint_pan_trip_ref.value === 0) {
              hint_pan_trip_ref.value = 1;
              if (idle_arrows_on_sv.value) {
                runOnJS(emit_idle_first_move)();
              }
            }
          }
          const half = layout_width.value / 2;
          const span_width = progress_width.value;
          if (span_width <= 0) {
            scroll_offset.value = half;
            return;
          }
          /** Always LTR scroll math: drag right → higher values (matches EN digit readout). */
          const hi = half;
          const lo = half - span_width;
          const raw = scroll_context.value + event.translationX;
          if (raw > hi) {
            scroll_offset.value = hi + (raw - hi) * EDGE_RUBBER;
          } else if (raw < lo) {
            scroll_offset.value = lo + (raw - lo) * EDGE_RUBBER;
          } else {
            scroll_offset.value = raw;
          }
        })
        .onEnd(event => {
          'worklet';
          if (idle_arrows_on_sv.value) {
            runOnJS(emit_idle_pan_end)();
          }
          const target = snapPoint(scroll_offset.value, event.velocityX, snap_offsets.value);
          scroll_offset.value = withSpring(target, snap_spring, finished => {
            'worklet';
            if (finished) {
              runOnJS(emit_gesture_active)(false);
            }
          });
        })
        .onFinalize((_event, success) => {
          'worklet';
          // Cancelled / failed pan — clear immediately (successful pans clear after snap spring).
          if (!success) {
            runOnJS(emit_gesture_active)(false);
          }
        }),
    // Stable emit_* bridges + spring config — never rebuild from parent value ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional shared-value gesture
    [emit_gesture_active, emit_idle_first_move, emit_idle_pan_end, snap_spring],
  );

  useAnimatedReaction(
    () => space_per_line.value,
    (curr, prev) => {
      if (curr !== prev) {
        const snapped = snapPoint(scroll_offset.value, 0, snap_offsets.value);
        scroll_offset.value = withSpring(snapped, snap_spring);
      }
    },
  );

  const indicator_line_width = Math.max(2.5, lineWidth * 1.55);
  const indicator_line_height = maxLineHeight * 1.62;
  const line_band_center_y = line_canvas_inner_pad + maxLineHeight / 2;
  const indicator_top = line_band_center_y - indicator_line_height / 2;

  const marker_color_sv = useSharedValue(markerColorProp ?? '#f5a623');

  useLayoutEffect(() => {
    marker_color_sv.value = markerColorProp ?? '#f5a623';
  }, [markerColorProp, marker_color_sv]);

  const r_indicator_style = useAnimatedStyle(() => {
    'worklet';
    if (indicatorColor) {
      return {
        backgroundColor: indicatorColor.value,
        transform: [{ scaleY: marker_pulse.value }],
      };
    }
    return {
      backgroundColor: marker_color_sv.value,
      transform: [{ scaleY: marker_pulse.value }],
    };
  }, [indicatorColor]);

  const on_track_layout = useCallback(
    (e: LayoutChangeEvent) => {
      const nw = e.nativeEvent.layout.width;
      if (nw > 0) {
        setMeasuredW(nw);
        layout_width.value = nw;
        has_applied_initial.value = false;
      }
    },
    [has_applied_initial, layout_width],
  );

  const start_icon = 'arrow-left';
  const end_icon = 'arrow-right';

  return (
    <View style={[S.track, layoutLtrStyle]} onLayout={on_track_layout}>
      <GestureDetector gesture={gesture}>
        <Animated.View style={{ height: scrollableAreaHeight, overflow: 'hidden', paddingTop: line_canvas_inner_pad }}>
          <View style={{ width: measured_w, height: maxLineHeight }}>
            <TickRuler
              lines_amount={linesAmount}
              spacing_px={spacing_px}
              scroll_offset={scroll_offset}
              max_line_height={maxLineHeight}
              min_line_height={minLineHeight}
              big_line_every={bigLineIndexOffset}
              line_value_base={lineValueBase}
              line_width={lineWidth}
              line_color={lineColor}
              big_line_color={bigLineColor}
              ruler_stops={rulerStops}
              viewport_width={measured_w}
              overscroll_ticks={overscroll_ticks}
            />
          </View>
        </Animated.View>
      </GestureDetector>

      <View pointerEvents="none" style={[S.markerSlot, { top: indicator_top }]}>
        <Animated.View
          style={[
            {
              width: indicator_line_width,
              height: indicator_line_height,
              borderRadius: Math.max(2, indicator_line_width),
            },
            r_indicator_style,
          ]}
        />
      </View>

      {idle_hints_on && show_idle_hints ? (
        <View
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
          style={StyleSheet.absoluteFill}>
          <Animated.View
            pointerEvents="none"
            style={[
              {
                position: 'absolute',
                left: IDLE_ARROW_INSET,
                top: line_band_center_y - IDLE_ARROW_PX / 2,
              },
              r_idle_hint_start_style,
            ]}>
            <SvgIcon name={start_icon} size={IDLE_ARROW_PX} color={idleArrowsColor} />
          </Animated.View>
          <Animated.View
            pointerEvents="none"
            style={[
              {
                position: 'absolute',
                right: IDLE_ARROW_INSET,
                top: line_band_center_y - IDLE_ARROW_PX / 2,
              },
              r_idle_hint_end_style,
            ]}>
            <SvgIcon name={end_icon} size={IDLE_ARROW_PX} color={idleArrowsColor} />
          </Animated.View>
        </View>
      ) : null}
    </View>
  );
};

const S = StyleSheet.create({
  track: {
    overflow: 'visible',
    position: 'relative',
    width: '100%',
  },
  markerSlot: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
});
